import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useEffect, useState } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import {
  ConnectPayoutSchema,
  ConnectPayoutPageSchema,
  ConnectCompositionPageSchema,
  type ConnectPayout,
  type ConnectedAccountScope,
} from './connect-receipts.contract'

export const PAYOUT_POLL_INTERVAL = 15_000
export type AdminPayoutFilters = Partial<ConnectedAccountScope> & {
  q?: string
  userId?: string
  status?: ConnectPayout['status']
  dateFrom?: string
  dateTo?: string
}
export function payoutIdentity(payout: ConnectPayout) {
  return [
    payout.account.platformId,
    payout.account.accountId,
    payout.account.livemode,
    payout.id,
  ].join(':')
}
function search(params: object) {
  const result = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') result.set(key, String(value))
  }
  return `?${result}`
}
function useActivePolling() {
  const [active, setActive] = useState(() => document.visibilityState !== 'hidden')
  useEffect(() => {
    const update = () => setActive(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', update)
    return () => document.removeEventListener('visibilitychange', update)
  }, [])
  return active ? PAYOUT_POLL_INTERVAL : false
}
export function useAdminPayouts(filters: AdminPayoutFilters, enabled = true) {
  const legacyInterval = useActivePolling()
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const refetchInterval = receiptsExperience ? false : legacyInterval
  return useInfiniteQuery({
    queryKey: ['admin-payouts', filters],
    queryFn: async ({ pageParam, signal }) =>
      ConnectPayoutPageSchema.parse(
        await apiFetch(
          `/admin/payouts${search({ ...filters, currency: 'BRL', limit: 25, cursor: pageParam })}`,
          { signal },
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => (page.hasMore ? page.nextCursor : null),
    enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
    ...(receiptsExperience ? { retry: 1 } : {}),
  })
}
export function useAdminPayout(payout: ConnectPayout) {
  const legacyInterval = useActivePolling()
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const refetchInterval = receiptsExperience ? false : legacyInterval
  return useQuery({
    queryKey: ['admin-payout', payoutIdentity(payout)],
    queryFn: async ({ signal }) =>
      ConnectPayoutSchema.parse(
        await apiFetch(`/admin/payouts/${encodeURIComponent(payout.id)}${search(payout.account)}`, {
          signal,
        }),
      ),
    refetchInterval,
    refetchIntervalInBackground: false,
    ...(receiptsExperience ? { retry: 1 } : {}),
  })
}
export function useAdminPayoutComposition(payout: ConnectPayout, enabled = true) {
  const legacyInterval = useActivePolling()
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const refetchInterval = receiptsExperience ? false : legacyInterval
  return useInfiniteQuery({
    queryKey: ['admin-payout-composition', payoutIdentity(payout)],
    queryFn: async ({ pageParam, signal }) =>
      ConnectCompositionPageSchema.parse(
        await apiFetch(
          `/admin/payouts/${encodeURIComponent(payout.id)}/composition${search({ ...payout.account, currency: 'BRL', limit: 25, cursor: pageParam })}`,
          { signal },
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => (page.hasMore ? page.nextCursor : null),
    enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
    ...(receiptsExperience ? { retry: 1 } : {}),
  })
}

export function useRefreshAdminReceipts() {
  const client = useQueryClient()
  return () =>
    Promise.all(
      ['admin-payouts', 'admin-payout', 'admin-payout-composition'].map((resource) =>
        client.invalidateQueries({ queryKey: [resource] }),
      ),
    )
}
